import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { motion } from 'framer-motion';
import { Heart, TrendingUp, BookOpen } from 'lucide-react';
import getLoansList from '../../api/GetLoansList';
import { setCurrentBook } from '../../store/slices/booksSlice';

const DEFAULT_COVER = '/images/default_cover_w299.jpg';

const BookCard = ({ title, author, cover, rating, borrows, delay, onClick }) => (
    <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay, duration: 0.4 }}
        whileHover={{ y: -5 }}
        onClick={onClick}
        className="group relative aspect-[2/3] rounded-xl overflow-hidden cursor-pointer bg-slate-800"
    >
        <img
            src={cover || DEFAULT_COVER}
            alt={title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = DEFAULT_COVER;
            }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/20 to-transparent opacity-80 group-hover:opacity-90 transition-opacity" />

        <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-2 group-hover:translate-y-0 transition-transform">
            <h4 className="text-white font-bold truncate">{title}</h4>
            <p className="text-slate-300 text-sm truncate">{author}</p>
            <div className="flex items-center justify-between mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <div style={{ visibility: rating == "true" ? 'visible' : 'hidden' }} className="flex items-center gap-1 text-yellow-400 text-xs">
                    <Heart size={12} fill="currentColor" />
                    {/*<span>{rating}</span>*/}

                </div>
                <div className="flex items-center gap-1 text-emerald-400 text-xs font-medium">
                    <TrendingUp size={12} />
                    <span>{borrows} emprunts</span>
                </div>
            </div>
        </div>
    </motion.div >
);

export default function PopularBooks({ onNavigate }) {
    const [popularBooks, setPopularBooks] = useState([]);
    const [loading, setLoading] = useState(true);
    const dispatch = useDispatch();

    const { items: allBooks } = useSelector((state) => state.books);
    const API_BASE = useSelector((state) => state.user.apiBase);
    const token = useSelector((state) => state.user.currentUser.token);
    const code_etablissement = useSelector((state) => state.user.currentUser.code_etablissement);

    const handleBookClick = (book) => {
        dispatch(setCurrentBook(book));
        if (onNavigate) {
            onNavigate('Books');
        }
    };

    useEffect(() => {
        const fetchPopularity = async () => {
            //console.log("PopularBooks: Fetching loans...", { API_BASE, token, code_etablissement });
            //console.log("PopularBooks: Checking data...", { API_BASE, token, code_etablissement, booksCount: allBooks.length });
            if (!API_BASE || !token || !code_etablissement || allBooks.length === 0) return;

            try {
                const loans = await getLoansList(API_BASE, token, code_etablissement);

                // Count loans per book ID
                const loanCounts = {};
                loans.forEach(loan => {
                    // Check if the book associated with this loan is reserved
                    const book = allBooks.find(b => (b.item_id || b.id) == loan.item_id);
                    if (book && book.status === 'Réservé') return; // Skip reservations

                    const id = loan.item_id;
                    loanCounts[id] = (loanCounts[id] || 0) + 1;
                });

                // Generate display data by mapping counts to book details
                const rankedBooks = allBooks
                    .map(book => ({
                        ...book,
                        borrows: loanCounts[book.item_id || book.id] || 0,
                        //rating: ''// (4.5 + Math.random() * 0.5).toFixed(1) // Keep mock rating for aesthetics until real rating exists
                    }))
                    .filter(book => book.borrows > 0) // Only show books that have been borrowed
                    .sort((a, b) => b.borrows - a.borrows)
                    .slice(0, 4); // Take top 4 for the grid

                setPopularBooks(rankedBooks);
            } catch (error) {
                console.error("Failed to fetch popular books:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchPopularity();
    }, [API_BASE, token, code_etablissement, allBooks]);

    return (
        <div className="bg-slate-900/50 backdrop-blur-xl border border-white/10 rounded-2xl p-6 h-full">
            <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                    <TrendingUp size={20} className="text-emerald-400" />
                    Tendances
                </h3>
                <span className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Les plus lus</span>
            </div>

            {loading ? (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 animate-pulse">
                    {[1, 2, 3, 4].map(i => (
                        <div key={i} className="aspect-[2/3] bg-white/5 rounded-xl" />
                    ))}
                </div>
            ) : popularBooks.length > 0 ? (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {popularBooks.map((book, index) => (
                        <BookCard
                            key={book.id || index}
                            title={book.title}
                            author={book.author}
                            cover={book.cover}
                            rating={book.rating}
                            borrows={book.borrows}
                            delay={index * 0.1}
                            onClick={() => handleBookClick(book)}
                        />
                    ))}
                </div>
            ) : (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                    <BookOpen size={40} className="text-slate-700 mb-3" />
                    <p className="text-slate-500 text-sm">Pas assez de données pour afficher les tendances</p>
                </div>
            )}
        </div>
    );
}
